import { Injectable, inject } from '@angular/core';
import { Observable, Subject, of, BehaviorSubject, EMPTY } from 'rxjs';
import {
  catchError,
  debounceTime,
  retry,
  switchMap,
  map,
  finalize,
  shareReplay,
} from 'rxjs/operators';
import { ApiService, ApiRequestOptions } from './api.service';
import {
  DashboardPlatform,
  WidgetPlatform,
  DashboardType,
  WidgetType,
} from '../enums/dashboard.enum';
import type {
  DashboardCardConfig,
  DashboardCardLayoutItem,
  DashboardWidgetCustomConfig,
  MonthlyReport1CardId,
} from '../enums/dashboard-cards.enum';
import {
  MONTHLY_REPORT_1_CARD_CONFIG,
  resolveMonthlyReport1CardFromWidgetKey,
} from '../enums/dashboard-cards.enum';

export interface DashboardRolePermission {
  role_id: number;
  can_view?: boolean;
  can_add?: boolean;
}

export interface DashboardApiWidget {
  /** Placement id when persisted; synthetic string when server hydrates from reset snapshot */
  id: string | number;
  widget_id: string | number;
  name: string;
  widget_key: string;
  type: string;
  position: number;
  is_visible: boolean;
  col_span?: number | null;
  row_span?: number | null;
  component_name?: string | null;
  default_cols?: number | null;
  min_cols?: number | null;
  source_page?: { id: number | string; name?: string } | null;
  custom_config?: DashboardWidgetCustomConfig | Record<string, unknown> | null;
  widget_role_permissions?: DashboardRolePermission[];
}

export interface DashboardMeta {
  id: number | string;
  user_id?: number;
  name?: string;
  type: string;
  page_id: number | string | null;
  platform: string;
  is_default?: boolean;
}

export interface DashboardLoadResponse {
  dashboard: DashboardMeta;
  widgets: DashboardApiWidget[];
}

/** Optional list endpoint shape */
export interface DashboardListResponse {
  dashboards: DashboardMeta[];
}

export interface WidgetPlatformConfig {
  platform: WidgetPlatform | string;
  component_name: string;
  default_cols: number;
  default_rows?: number;
  min_cols?: number;
  is_available: boolean;
  platform_metadata?: Record<string, unknown> | null;
}

export interface CreateWidgetDto {
  name: string;
  widget_key: string;
  type: WidgetType | string;
  category: string;
  is_default: boolean;
  default_position: number;
  metadata: Record<string, unknown> | null;
  platform_configs: WidgetPlatformConfig[];
}

export interface WidgetCatalogEntry {
  id: number;
  page_id: number | null;
  name: string;
  widget_key: string;
  type: WidgetType | string;
  is_default?: boolean;
  default_position?: number;
  widget_role_permissions?: DashboardRolePermission[];
  /** Joined platform row when API provides it */
  min_cols?: number | null;
  default_cols?: number | null;
  platform_configs?: WidgetPlatformConfig[];
}

export interface WidgetCatalogResponse {
  widgets: WidgetCatalogEntry[];
}

export function mergeDashboardLayoutWithDefaults<TCardId extends string = MonthlyReport1CardId>(
  apiWidgets: DashboardApiWidget[],
  frontendDefaults: DashboardCardConfig<TCardId>[],
  options?: {
    userRoleId?: number | null;
    /** Predicate to filter API widgets to only those belonging to this page.
     *  Defaults to accepting all widget_keys when omitted. */
    isCardId?: (key: string) => key is TCardId;
    /**
     * Maps API `widget_key` to template card id (e.g. backend `mr1_*` → MR1 `@switch` id).
     * When set, takes precedence over `isCardId` for row membership and `seen` tracking.
     *
     * @param key Raw `widget_key` from GET /dashboards.
     * @returns Template card id or null when the widget is not part of this page.
     */
    resolveCardId?: (key: string) => TCardId | null;
  },
): DashboardCardLayoutItem<TCardId>[] {
  const isCardId = options?.isCardId;
  const resolveCardId = options?.resolveCardId;
  const sorted = [...apiWidgets].sort((a, b) => a.position - b.position);
  const merged: DashboardCardLayoutItem<TCardId>[] = [];
  const seen = new Set<string>();

  for (const w of sorted) {
    let cardId: TCardId | null = null;
    if (resolveCardId) {
      cardId = resolveCardId(w.widget_key);
    } else if (isCardId) {
      if (!isCardId(w.widget_key)) {
        continue;
      }
      cardId = w.widget_key as TCardId;
    } else {
      cardId = w.widget_key as TCardId;
    }
    if (cardId == null) {
      continue;
    }
    if (!widgetAllowedForUser(w, options?.userRoleId)) {
      continue;
    }
    merged.push(apiRowToLayoutItem<TCardId>(w, cardId));
    seen.add(cardId as string);
  }

  merged.sort((a, b) => a.order - b.order);
  merged.forEach((row, idx) => {
    row.order = idx;
  });

  const defaultsSorted = [...frontendDefaults].sort(
    (a, b) => a.defaultOrder - b.defaultOrder,
  );
  let nextOrder = merged.length;
  for (const d of defaultsSorted) {
    if (seen.has(d.id)) continue;
    // API is the source of truth for visibility.
    // Fallback widgets (not returned by API) are hidden by default so only
    // API-provided widgets with is_visible:true appear on the dashboard.
    merged.push({
      id: d.id,
      order: nextOrder++,
      visible: false,
      widgetId: 0,
      colSpan: null,
      rowSpan: null,
    });
  }

  merged.forEach((row, idx) => {
    row.order = idx;
  });
  return merged;
}


function widgetAllowedForUser(
  w: DashboardApiWidget,
  userRoleId?: number | null,
): boolean {
  const perms = w.widget_role_permissions;
  if (!perms?.length) return true;
  if (userRoleId == null) return true;
  const row = perms.find((p) => p.role_id === userRoleId);
  if (!row) return false;
  return row.can_view !== false;
}

function coercePositiveWidgetId(value: string | number | undefined | null): number {
  if (value == null) {
    return 0;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : 0;
  }
  const s = String(value).trim();
  if (!/^\d+$/.test(s)) {
    return 0;
  }
  const n = Number(s);
  return Number.isSafeInteger(n) && n > 0 ? n : 0;
}

/**
 * Placement row id for PATCH size/custom-config; undefined for snapshot-only rows (synthetic `id`).
 *
 * @param id Value from GET widgets[].id.
 * @returns Numeric placement id when applicable.
 */
function parsePlacementIdFromApi(id: string | number | undefined | null): number | undefined {
  if (id == null) {
    return undefined;
  }
  if (typeof id === 'number') {
    return Number.isSafeInteger(id) && id > 0 ? id : undefined;
  }
  const s = String(id);
  if (s.startsWith('snapshot:')) {
    return undefined;
  }
  if (!/^\d+$/.test(s)) {
    return undefined;
  }
  const n = Number(s);
  return Number.isSafeInteger(n) ? n : undefined;
}

function coerceSourcePageId(value: number | string | null | undefined): number | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const n = Number(String(value).trim());
  return Number.isFinite(n) ? n : null;
}

function apiRowToLayoutItem<TCardId extends string = MonthlyReport1CardId>(
  w: DashboardApiWidget,
  cardId: TCardId,
): DashboardCardLayoutItem<TCardId> {
  const allowedRoleIds =
    w.widget_role_permissions
      ?.filter((p) => p.can_view !== false)
      .map((p) => p.role_id) ?? undefined;
  const cc = w.custom_config as DashboardWidgetCustomConfig | null | undefined;
  return {
    id: cardId,
    order: w.position,
    visible: w.is_visible,
    widgetId: coercePositiveWidgetId(w.widget_id),
    placementId: parsePlacementIdFromApi(w.id),
    colSpan: w.col_span,
    rowSpan: w.row_span,
    sourcePageId: coerceSourcePageId(w.source_page?.id),
    sourcePageName: w.source_page?.name ?? null,
    customConfig: (cc && typeof cc === 'object' ? cc : null) as DashboardWidgetCustomConfig | null,
    minCols: w.min_cols ?? null,
    defaultCols: w.default_cols ?? null,
    allowedRoleIds,
  };
}

export function layoutItemsToWidgetIds(
  items: DashboardCardLayoutItem[],
): number[] {
  return items
    .sort((a, b) => a.order - b.order)
    .map((i) => i.widgetId)
    .filter((id) => id > 0);
}

export function cloneLayoutItems<TCardId extends string = MonthlyReport1CardId>(
  items: DashboardCardLayoutItem<TCardId>[],
): DashboardCardLayoutItem<TCardId>[] {
  return items.map((i) => ({ ...i }));
}

@Injectable({ providedIn: 'root' })
export class DashboardLayoutService {
  private api = inject(ApiService);

  private readonly defaultOptions: ApiRequestOptions = {
    showLoader: false,
    showErrorToaster: true,
  };

  private readonly quietOptions: ApiRequestOptions = {
    showLoader: false,
    showErrorToaster: false,
  };

  private saveSubject = new Subject<{ dashboardId: number; widgetIds: number[] }>();
  private saving = new BehaviorSubject<boolean>(false);

  readonly saving$ = this.saving.asObservable();
 
  private readonly _inFlightLoads = new Map<string, Observable<DashboardLoadResponse>>();

  /** Same dedupe pattern as {@link _inFlightLoads}, scoped to `listDashboards()`. */
  private readonly _inFlightLists = new Map<string, Observable<DashboardMeta[]>>();

  /** In-flight cache for `loadByDashboardId()` keyed by `<dashId>:<platform>`. */
  private readonly _inFlightLoadById = new Map<string, Observable<DashboardLoadResponse>>();

  /** In-flight cache for `getWidgetCatalog()` keyed by `<pageId>:<platform>`. */
  private readonly _inFlightCatalog = new Map<string, Observable<WidgetCatalogEntry[]>>();

  /** In-flight cache for the page-less mixed dashboard load
   *  (`loadMixed()`), keyed by `<platform>:<productId>:<mixed>`. */
  private readonly _inFlightMixedLoads = new Map<string, Observable<any>>();

  constructor() {
    this.saveSubject
      .pipe(
        debounceTime(500),
        switchMap(({ dashboardId, widgetIds }) =>
          this.putWidgetOrderImmediate(dashboardId, widgetIds).pipe(
            retry({ count: 1, delay: 400 }),
            catchError((err) => {
              console.error('Dashboard save failed', err);
              return EMPTY;
            }),
            finalize(() => this.saving.next(false)),
          ),
        ),
      )
      .subscribe();
  }

  /**
   * Load a specific saved dashboard when backend supports GET by id.
   *
   * Concurrent callers requesting the same `(dashboardId, platform)`
   * pair share a single HTTP roundtrip via {@link _inFlightLoadById};
   * the entry is cleared as soon as the response (or error) settles.
   */
  loadByDashboardId(
    dashboardId: number,
    platform: 'web' | 'mobile',
  ): Observable<DashboardLoadResponse> {
    const cacheKey = `${dashboardId}:${platform}`;
    const existing = this._inFlightLoadById.get(cacheKey);
    if (existing) return existing;

    const request$ = this.api
      .get<DashboardLoadResponse>(
        `/dashboards/${dashboardId}`,
        { platform },
        this.defaultOptions,
      )
      .pipe(
        finalize(() => this._inFlightLoadById.delete(cacheKey)),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    this._inFlightLoadById.set(cacheKey, request$);
    return request$;
  }

  load(
    pageId: number,
    platform: 'web' | 'mobile',
    isMixed?: boolean,
    productId?: string | number | null,
  ): Observable<DashboardLoadResponse> {
     
    if (isMixed && productId == null) {
      return EMPTY as unknown as Observable<DashboardLoadResponse>;
    }

    // `page_id` is always sent — both non-mixed and mixed dashboards need it so the backend can scope the layout to this page.
    const params: Record<string, string | number | boolean> = {
      page_id: pageId,
      platform,
    }; 
    if (isMixed) {
      params['mixed'] = true;
      params['product_id'] = productId as string | number;
    } 
    const cacheKey = JSON.stringify(params);
    const existing = this._inFlightLoads.get(cacheKey);
    if (existing) return existing;

    const request$ = this.api
      .get<DashboardLoadResponse>('/dashboards', params, this.defaultOptions)
      .pipe(
        finalize(() => this._inFlightLoads.delete(cacheKey)),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    this._inFlightLoads.set(cacheKey, request$);
    return request$;
  }
 
  listDashboards(
    pageId: number | null,
    platform: 'web' | 'mobile',
    mixed: boolean = false,
    productId?: string | number | null,
  ): Observable<DashboardMeta[]> {
    
    if (mixed && productId == null) {
      return of([]);
    }

    // Build the params. `page_id` is included only when non-null so we 
    const params: Record<string, string | number | boolean> = {
      platform,
      mixed,
    };
    if (pageId != null) params['page_id'] = pageId;
    if (mixed && productId != null) params['product_id'] = productId; 
    const cacheKey = 'list:' + JSON.stringify(params);
    const existing = this._inFlightLists.get(cacheKey);
    if (existing) return existing;

    const request$ = this.api
      .get<DashboardListResponse | DashboardMeta[]>(
        '/dashboards',
        params,
        this.quietOptions,
      )
      .pipe(
        map((res: any) => {
          if (Array.isArray(res)) return res;
          if (res?.dashboards && Array.isArray(res.dashboards))
            return res.dashboards;
          return [];
        }),
        catchError(() => of<DashboardMeta[]>([])),
        finalize(() => this._inFlightLists.delete(cacheKey)),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    this._inFlightLists.set(cacheKey, request$);
    return request$;
  }

  getWidgetCatalog(
    pageId: number,
    platform: 'web' | 'mobile',
  ): Observable<WidgetCatalogEntry[]> {
    const cacheKey = `${pageId}:${platform}`;
    const existing = this._inFlightCatalog.get(cacheKey);
    if (existing) return existing;

    const request$ = this.api
      .get<WidgetCatalogResponse>(
        `/catalog/pages/${pageId}/widgets`,
        { platform },
        this.defaultOptions,
      )
      .pipe(
        map((r: any) => (Array.isArray(r) ? r : r?.widgets ?? [])),
        catchError(() => of([] as WidgetCatalogEntry[])),
        finalize(() => this._inFlightCatalog.delete(cacheKey)),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    this._inFlightCatalog.set(cacheKey, request$);
    return request$;
  }

  /**
   * Page-less mixed dashboard load — `GET /dashboards?platform=&mixed=&product_id=`
   * (no `page_id`). Used by the My Dashboard page, which groups widgets
   * across pages client-side instead of asking the backend per page.
   *
   * Mirrors {@link load}'s in-flight dedup so two rapid invocations
   * (e.g. product-switch effect + initial `ngOnInit`) collapse to one
   * network call.
   */
  loadMixed<T = unknown>(
    productId: string | number,
    platform: 'web' | 'mobile' = 'web',
    mixed: boolean = true,
  ): Observable<T | null> {
    const cacheKey = `${platform}:${productId}:${mixed}`;
    const existing = this._inFlightMixedLoads.get(cacheKey) as
      | Observable<T | null>
      | undefined;
    if (existing) return existing;

    const request$ = this.api
      .get<T>(
        '/dashboards',
        { platform, mixed, product_id: productId },
        this.defaultOptions,
      )
      .pipe(
        catchError(() => of(null)),
        finalize(() => this._inFlightMixedLoads.delete(cacheKey)),
        shareReplay({ bufferSize: 1, refCount: true }),
      ) as Observable<T | null>;
    this._inFlightMixedLoads.set(cacheKey, request$);
    return request$;
  }

  /** In-flight observable for {@link getCatalogProducts}; concurrent callers share one HTTP roundtrip. */
  private _catalogProductsInFlight: Observable<any[]> | null = null;

  getCatalogProducts(): Observable<any[]> { 
    if (this._catalogProductsInFlight) return this._catalogProductsInFlight;

    this._catalogProductsInFlight = this.api.get<any[]>('/catalog/products').pipe(
      catchError(() => of([])),
      finalize(() => { this._catalogProductsInFlight = null; }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    return this._catalogProductsInFlight;
  }

  /** Debounced save — use after drag-drop */
  save(dashboardId: number, widgetIds: number[]): void {
    this.saving.next(true);
    this.saveSubject.next({ dashboardId, widgetIds });
  }

  putWidgetOrderImmediate(
    dashboardId: number,
    widgetIds: number[],
  ): Observable<unknown> {
    return this.api.put(
      `/dashboards/${dashboardId}/widgets`,
      {
        widget_ids: widgetIds.map((id) => String(id)),
      },
      this.defaultOptions,
    );
  }

  // toggleVisibility(
  //   dashboardId: number,
  //   placementOrWidgetId: number,
  // ): Observable<unknown> {
  //   return this.api.patch(
  //     `/dashboards/${dashboardId}/widgets/${placementOrWidgetId}/visibility`,
  //     {},
  //     this.defaultOptions,
  //   );
  // }
  // payload added in toggleVisibility
  toggleVisibility(
    dashboardId: number,
    placementOrWidgetId: number,
    // prism-new requires is_visible (it sets it; the old API toggled and used the rest for a new placement).
    payload?: {
      col_span?: number;
      row_span?: number;
      is_visible: boolean;
      custom_config?: Record<string, any>;
    },
  ): Observable<unknown> {
    return this.api.patch(
      `/dashboards/${dashboardId}/widgets/${placementOrWidgetId}/visibility`,
      payload ?? {},
      this.defaultOptions,
    );
  }

  updateWidgetSize(
    dashboardId: number,
    placementId: number,
    colSpan: number,
    rowSpan?: number | null,
  ): Observable<unknown> {
    return this.api.patch(
      `/dashboards/${dashboardId}/widgets/${placementId}/size`,
      {
        col_span: colSpan,
        row_span: rowSpan ?? null,
      },
      this.quietOptions,
    );
  }

  patchWidgetCustomConfig(
    dashboardId: number,
    placementId: number,
    customConfig: DashboardWidgetCustomConfig | null,
  ): Observable<unknown> {
    return this.api.patch(
      `/dashboards/${dashboardId}/widgets/${placementId}/custom-config`,
      { custom_config: customConfig },
      { ...this.quietOptions, showErrorToaster: true },
    );
  }

  reset(dashboardId: number): Observable<DashboardLoadResponse | unknown> {
    return this.api.post(
      `/dashboards/${dashboardId}/reset`,
      {},
      this.defaultOptions,
    );
  }

  createDashboard(body: {
    name: string;
    type: 'product_page' | 'mixed';
    platform: 'web' | 'mobile';
    page_id?: number | null;
    product_id?: number | string | null;
    widget_ids?: Array<{
      widget_id: number;
      source_page_id?: number | null;
      product_id?: number | string | null;
      col_span?: number | null;
      row_span?: number | null;
      custom_config?: Record<string, any> | null;
    }>;
  }): Observable<DashboardLoadResponse> {
    return this.api.post<DashboardLoadResponse>(
      '/dashboards',
      body,
      this.defaultOptions,
    );
  }

  addWidget(
    dashboardId: number,
    body: {
      widget_id: number;
      source_page_id?: number | null;
      col_span?: number | null;
      row_span?: number | null;
      custom_config?: Record<string, any> | null;
    }
  ): Observable<DashboardLoadResponse> {
    return this.api.post<DashboardLoadResponse>(
      `/dashboards/${dashboardId}/widgets`,
      body,
      this.defaultOptions,
    );
  }

  deleteWidget(
    dashboardId: number,
    widgetId: number
  ): Observable<unknown> {
    return this.api.delete(
      `/dashboards/${dashboardId}/widgets/${widgetId}`,
      this.defaultOptions,
    );
  }

  batchUpdateWidgets(
    dashboardId: number,
    body: {
      widgets: Array<{
        widget_id: number | string;
        source_page_id?: number | string | null;
        product_id?: number | string | null;
        col_span?: number | null;
        row_span?: number | null;
        is_visible?: boolean;
        custom_config?: Record<string, any> | null;
      }>;
    }
  ): Observable<DashboardLoadResponse> {
    return this.api.post<DashboardLoadResponse>(
      `/dashboards/${dashboardId}/widgets/batch`,
      body,
      this.defaultOptions,
    );
  }

  setDefaultDashboard(dashboardId: number): Observable<unknown> {
    return this.api.patch(
      `/dashboards/${dashboardId}/default`,
      {},
      this.quietOptions,
    );
  }

  deleteDashboard(dashboardId: number): Observable<unknown> {
    return this.api.delete(`/dashboards/${dashboardId}`, this.defaultOptions);
  }

  createCatalogWidget(
    pageId: string | number,
    body: CreateWidgetDto,
  ): Observable<WidgetCatalogEntry> {
    return this.api.post<WidgetCatalogEntry>(
      `/catalog/pages/${pageId}/widgets`,
      body,
      this.defaultOptions,
    );
  }

  patchWidgetPlatformConfig(
    widgetId: string | number,
    platform: string,
    body: Partial<WidgetPlatformConfig>,
  ): Observable<WidgetPlatformConfig> {
    return this.api.patch<WidgetPlatformConfig>(
      `/catalog/widgets/${widgetId}/platforms/${platform}`,
      body,
      this.defaultOptions,
    );
  }

  monthlyReport1Defaults(): DashboardCardConfig<MonthlyReport1CardId>[] {
    return MONTHLY_REPORT_1_CARD_CONFIG;
  }

  mergeWithDefaults(
    widgets: DashboardApiWidget[],
    userRoleId?: number | null,
  ): DashboardCardLayoutItem<MonthlyReport1CardId>[] {
    return mergeDashboardLayoutWithDefaults(
      widgets,
      MONTHLY_REPORT_1_CARD_CONFIG,
      { userRoleId, resolveCardId: resolveMonthlyReport1CardFromWidgetKey },
    );
  }
}
